import React, { memo, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { useAppSelector } from 'store/hooks';
import { CardView, rem, Spacer } from 'design-system';
import Amount from 'design-system/Amount';
import { IconRocket } from 'design-system/icons/IconRocket';
import { SpaceUserAvatar } from 'design-system/SpaceUserAvatar';
import Text from 'design-system/Text';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { selectAutosaveStatusObj } from 'reducers/selectors';
import { assertUnreachable } from 'utils';
import createStyleSheets from 'utils/createStyleSheets';
import { formatDateForItem } from 'utils/formatting';
import Modal from 'utils/packages/Modal';
import TouchableBounce from 'utils/packages/TouchableBounce';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { showUnavailableOnWeb } from 'utils/webGate';
import { isWeb } from '../../../constants';
import AutosaveActivityStatusView from './AutosaveActivityStatusView';
const AutosaveActivityCard = ({ activity, withCard, btnStyles, userId, disabled, hideOwnerInfo, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const navigation = useMainStackNavigation();
    const autosaveStatusObj = useAppSelector(selectAutosaveStatusObj);
    const handlePress = useCallback(() => {
        switch (activity.status) {
            case 'FAILED': {
                if (activity.collectionSkipReason === 'DESTINATIONS_UNAVAILABLE') {
                    Modal.showConfirmation({
                        title: 'Autosave not collected',
                        subtitle: 'None of your destinations could receive this deposit. Review your destinations before the next collection.',
                        confirmButtonTitle: 'Update allocations',
                        onConfirm: () => {
                            if (showUnavailableOnWeb('emma://save?tab=pots', 'Update allocations')) {
                                return;
                            }
                            navigation.navigate('AutosaveDistribution', { isEditFlow: true });
                        },
                    });
                    break;
                }
                Modal.showConfirmation({
                    title: 'Autosave cancelled',
                    subtitle: [
                        'We couldn’t move money this time. Your bank declined the payment or there wasn’t enough money in your account.',
                        'Please check your balance or contact your bank if this continues.',
                    ],
                    confirmButtonTitle: 'Got it',
                });
                break;
            }
            case 'FAILED_OB_CONNECTION': {
                Modal.showConfirmation({
                    title: 'Autosave cancelled',
                    subtitle: [
                        'Autosave couldn’t run because your bank connection has expired.',
                        'Reconnect your bank to keep using AI savings and Round Ups.',
                    ],
                    confirmButtonTitle: autosaveStatusObj?.actionRequired ? 'Reconnect bank' : 'Got it',
                    onConfirm: autosaveStatusObj?.actionRequired
                        ? () => {
                            if (showUnavailableOnWeb('emma://save?tab=pots', 'Reconnect bank')) {
                                return;
                            }
                            navigation.navigateDeprecated('AutosaveSelectBank');
                        }
                        : undefined,
                });
                break;
            }
            case 'FAILED_BELOW_THRESHOLD': {
                Modal.showConfirmation({
                    title: 'Autosave cancelled',
                    subtitle: [
                        'Based on your recent activity, there wasn’t enough available to save.',
                        'We’ll try again automatically next time.',
                    ],
                    confirmButtonTitle: 'Got it',
                });
                break;
            }
            case 'SKIPPED': {
                Modal.showConfirmation({
                    title: 'Autosave skipped',
                    subtitle: 'You’ve opted to skip the scheduled direct debit collection, so the amount has not been deducted. The option to reverse the skip is only accessible for the first 24 hours.',
                    confirmButtonTitle: 'Got it',
                });
                break;
            }
            case 'COMPLETED':
            case 'PENDING': {
                navigation.navigateDeprecated('AutosaveActivityTimeline', {
                    activity: activity,
                });
                break;
            }
            default: {
                assertUnreachable(activity.status);
                break;
            }
        }
    }, [activity, autosaveStatusObj?.actionRequired]);
    const content = useMemo(() => (<View style={styles.mainRow}>
        <View style={styles.iconContainer}>
          <IconRocket colorKey="brand"/>
        </View>
        <Spacer.W16 />
        <View style={styles.flex}>
          <View style={styles.row}>
            <View style={styles.flex}>
              <Text Text-16>Autosave</Text>
              <AutosaveActivityStatusView status={activity.status}/>
              {!hideOwnerInfo && <SpaceUserAvatar xxSmall userId={userId} style={styles.spaceUserAvatar}/>}
            </View>
            <View style={styles.rightContent}>
              <Amount showSign Numbers-16 value={activity.amount} style={activity.status !== 'PENDING' && activity.status !== 'COMPLETED' && styles.skipped} color={activity.status !== 'PENDING' && activity.status !== 'COMPLETED'
            ? colors.text.tertiary
            : colors.text.income}/>
              <Text TextThin-14 Secondary>
                {formatDateForItem(new Date(activity.timestamp))}
              </Text>
            </View>
          </View>
        </View>
      </View>), [
        activity.amount,
        activity.status,
        activity.timestamp,
        colors.text.income,
        colors.text.tertiary,
        hideOwnerInfo,
        styles.flex,
        styles.iconContainer,
        styles.mainRow,
        styles.rightContent,
        styles.row,
        styles.skipped,
        styles.spaceUserAvatar,
        userId,
    ]);
    if (withCard) {
        return (<TouchableBounce onPress={handlePress}>
        <CardView noMarginTop noMarginBottom style={styles.card}>
          {content}
        </CardView>
      </TouchableBounce>);
    }
    return (<TouchableHighlight hoverOnLight onCard={isWeb} disabled={disabled} onPress={handlePress} style={isWeb ? undefined : btnStyles} activeOpacity={1} underlayColor={colors.background.underlay}>
      {content}
    </TouchableHighlight>);
};
const styleSet = createStyleSheets((colors) => ({
    skipped: {
        textDecorationStyle: 'solid',
        textDecorationLine: 'line-through',
    },
    flex: {
        flex: 1,
    },
    // On web the row is padded by 8 horizontally, so the card adds the other 8. The row
    // already has 16 above and below, so the card adds nothing vertically.
    card: {
        ...(isWeb && { paddingHorizontal: rem(8) }),
    },
    mainRow: {
        padding: rem(16),
        ...(isWeb && { paddingHorizontal: rem(8) }),
        alignItems: 'center',
        flexDirection: 'row',
    },
    row: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    iconContainer: {
        width: rem(42),
        height: rem(42),
        borderWidth: rem(1),
        borderRadius: rem(42),
        alignItems: 'center',
        justifyContent: 'center',
        borderColor: colors.borders.logoStroke,
        backgroundColor: colors.background.brandLight,
    },
    spaceUserAvatar: {
        paddingTop: rem(2),
    },
    rightContent: {
        alignItems: 'flex-end',
    },
}));
export default memo(AutosaveActivityCard);
